// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { APP_HERO_PRODUCTS } from '@white-label-configuration/constants';

// Types
import type { ManageBookingOriginalOrderItem, SearchProducts, productTypeId, ProductOptions, Bundle } from '@white-label-types/parking-booking';
import type { SearchProductData } from '@white-label-types/pusher';
import type { TerminalFlight } from '@white-label-types/terminal-flight';

// Mocks
import { mockBundleResults } from './mock-bundle-results';


function processSearchItem(item: SearchProductData, isBundleItem: boolean): SearchProducts {
  const productHeader = {
    id: item.search_product_result_id,
    inventory_id: item.inventory_id,
    product_code: item.product_code,
    inventory_provider: item.inventory_provider,
  };

  const isParking = productHeader.product_code === APP_HERO_PRODUCTS.PARKING;
  const isExtras = productHeader.product_code === APP_HERO_PRODUCTS.EXTRAS;
  const isTravelInsurance = productHeader.product_code === APP_HERO_PRODUCTS.TRAVEL_INSURANCE;

  const inventory_options = item.inventory_options
    .filter((inventory_option) => isParking || isTravelInsurance || inventory_option?.details?.is_parent);
  const productOptions = [...inventory_options];

  let subOptionsHaveDiscount = false;
  if (isExtras && productOptions[0]?.sub_options !== undefined && productOptions[0].sub_options.length > 0) {
    subOptionsHaveDiscount = productOptions[0]?.sub_options.some((option) => !!option.totals.discount);
  }

  let processedBundlesItems: Bundle[] = [];

  if (getAppVariable('is_sell_bundles_enabled')) {
    const bundles = item.inventory_item?.bundles || [];

    // TODO: Remove when feature flag is archived
    // Totals not yet present in the bundle items, mocked unless they are present in the future
    const bundlesWithMockedTotals = bundles.map(bundle => {
      let totals
      if ('totals' in bundle) totals = bundle.totals;
      else totals = mockBundleResults[0].totals
      return { ...bundle, totals }
    });

    // @ts-expect-error - it is unclear why the use of the "mocked totals" is necessary, I don't want to change and break something.
    processedBundlesItems = bundles.length && !isBundleItem ? processBundleItems(bundlesWithMockedTotals, item) : [];
  }

  const primaryItemHasDiscount = (!isExtras && !!productOptions[0].totals?.discount);
  const bundleItemHasDiscount = (isBundleItem && !!productOptions[0].totals?.discount);

  const hasDiscount = primaryItemHasDiscount || (isExtras && subOptionsHaveDiscount) || bundleItemHasDiscount;

  const entryExitInfo = {
    entryDate: item.inventory_item.entry_date ?? item.details?.entry_date ?? '',
    exitDate: item.inventory_item.exit_date ?? item.details?.exit_date ?? '',
  }

  const resultItem: SearchProducts = {
    ...item.inventory_item,
    id: productOptions[0].id,
    cart_item_id: '',
    productHeader,
    productOptions,
    hasDiscount,
    bundles: processedBundlesItems,
    entryExitInfo,
  };
  return resultItem;
}

function processBundleItems(bundles: Bundle[], primaryProduct: SearchProductData) {
  const defaultBundle = {
    name: primaryProduct.inventory_item.name,
    id: primaryProduct.search_product_result_id,
    products: [],
    totals: {
      ...primaryProduct.inventory_options[0].totals,
      exchange: null,
    },
  };

  const processedBundleItems = [
    defaultBundle,
    ...bundles
  ];

  processedBundleItems.forEach((bundleItem) => {
    bundleItem.products = [
      // @ts-expect-error - needs better understanding of the data structure to fix
      primaryProduct,
      ...bundleItem.products
    ]
    bundleItem.products = bundleItem.products.map((product) => {
      return processSearchItem(product, true);
    })
  });

  return processedBundleItems;
}

// this is to generate Search item compatibe strtucture out of manage booking item to display previously booked items + info modal on manage booking page
export function processManageBookingItem(item: ManageBookingOriginalOrderItem): SearchProducts {
  const productOptions = item.inventory_option as ProductOptions;
  productOptions.totals = item.totals as ProductOptions['totals'];

  const productHeader = {
    inventory_id: item.inventory_id,
    product_code: item.product_code
  }

  const entryExitInfo = {
    entryDate: item.details?.entry_date ?? '',
    exitDate: item.details?.exit_date ?? '',
  }

  const resultItem: SearchProducts = {
    ...item.inventory_item,
    id: productOptions.id,
    name: item.inventory_item.display_name,
    productHeader,
    productOptions: [productOptions],
    hasDiscount: !!productOptions.totals?.discount,
    entryExitInfo,
    capacity: {left: 99, message_threshold: 0},
    is_featured: false,
    is_available: false,
    upgrade: {
      is_available: false,
      is_for_upgrade_only: false,
      product_id: null
    },
    is_customer_keeps_keys: false,
    is_covered: false
  };
  return resultItem;
}

function addItemUpgradeObject(item: SearchProducts, processedResults: SearchProducts[]) {
  const resultItem = { ...item };
  const childId = resultItem?.upgrade?.product_id ? +resultItem.upgrade.product_id : null;
  const hasUpgrade = resultItem?.upgrade?.is_available;
  const childObject = processedResults.find((i) => i.id === childId);
  const isNestedParkingLotExists = childId && hasUpgrade;

  if (isNestedParkingLotExists && childObject) {
    resultItem.upgradeObject = {
      ...childObject,
      delta: childObject.productOptions[0].totals.channel_based.total_discounted
        - resultItem.productOptions[0].totals.channel_based.total_discounted,
    };
  }

  return resultItem;
}

export function buildSearchResults(searchResults: SearchProductData[] = [], productId: productTypeId = 'lounges'): SearchProducts[] {
  const processedResults: SearchProducts[] = searchResults.map((item) => processSearchItem(item, false));
  const processedResultsWithUpgrade: SearchProducts[] = processedResults.map(
    (item) => addItemUpgradeObject(item, processedResults),
  );
  const validResults = processedResultsWithUpgrade
  // check this when we want to have upgrate on premiAir
    .filter((item) => productId !== 'parking' || !item?.upgrade?.is_for_upgrade_only);

  return validResults;
}


export function buildSearchResultsForTerminalFlights(searchResults: SearchProductData[]): TerminalFlight[] {

  const flights: Array<TerminalFlight> = [];

  searchResults.forEach((item) => {
    if(item.inventory_provider === 'copenhagen-optimization'){
      flights.push(item as TerminalFlight);
    }
  });

  return flights;

}
